/**
 * ConcoursDetail - Detailed view of a single concours
 */
import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  Bookmark,
  Calendar,
  CheckCircle,
  DollarSign,
  Edit,
  ExternalLink,
  Eye,
  FileText,
  Globe,
  Lightbulb,
  Mail,
  Phone,
  Send,
  Star,
  Users
} from 'lucide-react';
import React from 'react';
import {
  Concours,
  CONCOURS_STATUS_COLORS,
  CONCOURS_STATUS_LABELS,
} from '../../types/concours';
import { migrateCalendar } from '../../utils/calendarMigration';

interface ConcoursDetailProps {
  concours: Concours;
  onEdit: () => void;
  onBack: () => void;
  onPublish: () => void;
  onArchive: () => void;
}

const ConcoursDetail: React.FC<ConcoursDetailProps> = ({
  concours,
  onEdit,
  onBack,
  onPublish,
  onArchive,
}) => {
  // Migrate old calendar format to new format, extracting from registration_steps and exams if needed
  const calendar = migrateCalendar(concours.calendar, concours);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Retour à la liste
        </button>
        <div className="flex items-center gap-2">
          {concours.status === 'draft' && (
            <button
              onClick={onPublish}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Send className="w-4 h-4" />
              Publier
            </button>
          )}
          {(concours.status === 'completed' || concours.status === 'registration_closed') && (
            <button
              onClick={onArchive}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
            >
              <Archive className="w-4 h-4" />
              Archiver
            </button>
          )}
          <button
            onClick={onEdit}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </button>
        </div>
      </div>

      {/* Main Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Banner */}
        {concours.banner_url && (
          <div className="h-48 bg-gradient-to-r from-indigo-600 to-purple-600 relative">
            <img
              src={concours.banner_url}
              alt={concours.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${CONCOURS_STATUS_COLORS[concours.status]}`}>
                  {CONCOURS_STATUS_LABELS[concours.status]}
                </span>
                {concours.is_featured && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <Star className="w-3 h-3" />
                    Mis en avant
                  </span>
                )}
                {concours.is_popular && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Populaire
                  </span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{concours.title}</h1>
              {concours.subtitle && (
                <p className="text-gray-600 mb-2">{concours.subtitle}</p>
              )}
              <p className="text-sm text-gray-500">
                {concours.institution} • {concours.year}
                {concours.organizing_body && ` • ${concours.organizing_body}`}
              </p>
            </div>

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {concours.view_count || 0} vues
              </div>
              <div className="flex items-center gap-1">
                <Bookmark className="w-4 h-4" />
                {concours.save_count || 0} sauvegardes
              </div>
            </div>
          </div>

          {/* Classification Tags */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm">
              {concours.category}
            </span>
            {concours.subcategory && (
              <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                {concours.subcategory}
              </span>
            )}
            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
              Niveau {concours.level}
            </span>
            {concours.type && (
              <span className="px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm">
                {concours.type === 'direct' ? 'Concours direct' :
                  concours.type === 'professionnel' ? 'Concours professionnel' :
                    'Concours interne'}
              </span>
            )}
            {concours.is_regionalized && (
              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                Régionalisé
              </span>
            )}
          </div>

          {/* Description */}
          {concours.description && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-gray-700 whitespace-pre-wrap">{concours.description}</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Eligibility */}
        {concours.eligibility && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-600" />
              Conditions d'éligibilité
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Nationalité</span>
                <span className="font-medium">{concours.eligibility.nationality}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Âge</span>
                <span className="font-medium">
                  {concours.eligibility.min_age} - {concours.eligibility.max_age} ans
                </span>
              </div>
              {concours.eligibility.age_note && (
                <p className="text-sm text-gray-500 italic">{concours.eligibility.age_note}</p>
              )}

              {concours.eligibility.required_diplomas && concours.eligibility.required_diplomas.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-2">Diplômes requis :</p>
                  <ul className="space-y-1">
                    {concours.eligibility.required_diplomas.map((diploma, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>
                          {diploma.name}
                          {diploma.full_name && ` (${diploma.full_name})`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {concours.eligibility.other_conditions && concours.eligibility.other_conditions.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-2">Autres conditions :</p>
                  <ul className="space-y-1">
                    {concours.eligibility.other_conditions.map((condition, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{condition}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar */}
        {calendar && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Calendrier
            </h2>
            <div className="space-y-3">
              {calendar.events && calendar.events.length > 0 ? (
                calendar.events.map((event, index) => (
                  <div key={index} className="flex justify-between items-start">
                    <span className="text-gray-600 font-medium">{event.label}</span>
                    <span className="text-sm text-right">
                      {event.type === 'period' || event.start ? (
                        <>
                          {formatDate(event.start)} - {formatDate(event.end)}
                        </>
                      ) : (
                        formatDate(event.date)
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 italic">Aucun événement programmé</p>
              )}
            </div>
          </div>
        )}

        {/* Fees */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            Frais
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Inscription</span>
              <span className="font-medium">{formatCurrency(concours.registration_fee)}</span>
            </div>
            {concours.medical_exam_fee && (
              <div className="flex justify-between">
                <span className="text-gray-600">Visite médicale</span>
                <span className="font-medium">{formatCurrency(concours.medical_exam_fee)}</span>
              </div>
            )}
            {concours.other_fees && concours.other_fees.map((fee, idx) => (
              <div key={idx} className="flex justify-between">
                <span className="text-gray-600">{fee.name}</span>
                <span className="font-medium">{formatCurrency(fee.amount)}</span>
              </div>
            ))}
            {concours.payment_methods && concours.payment_methods.length > 0 && (
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Moyens de paiement : {concours.payment_methods.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Links & Contacts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-indigo-600" />
            Liens & Contacts
          </h2>

          {concours.links && (
            <div className="space-y-2 mb-4">
              {concours.links.official && (
                <a
                  href={concours.links.official}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Site officiel
                </a>
              )}
              {concours.links.registration && (
                <a
                  href={concours.links.registration}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700"
                >
                  <ExternalLink className="w-4 h-4" />
                  Inscription
                </a>
              )}
            </div>
          )}

          {concours.contacts && concours.contacts.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-gray-100">
              {concours.contacts.map((contact, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-600">
                  {contact.type === 'phone' && <Phone className="w-4 h-4" />}
                  {contact.type === 'email' && <Mail className="w-4 h-4" />}
                  {contact.type === 'whatsapp' && <Phone className="w-4 h-4" />}
                  <span>{contact.label && `${contact.label}: `}{contact.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tips & Warnings */}
        {((concours.tips && concours.tips.length > 0) || (concours.warnings && concours.warnings.length > 0)) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {concours.tips && concours.tips.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    Conseils
                  </h3>
                  <ul className="space-y-2">
                    {concours.tips.map((tip, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {concours.warnings && concours.warnings.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    Avertissements
                  </h3>
                  <ul className="space-y-2">
                    {concours.warnings.map((warning, idx) => (
                      <li key={idx} className="text-sm text-red-600 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Required Documents */}
      {concours.required_documents && concours.required_documents.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Documents requis
          </h2>
          <div className="space-y-6">
            {concours.required_documents.map((category, idx) => (
              <div key={idx}>
                <h3 className="font-medium text-gray-800 mb-3">{category.category}</h3>
                <ul className="space-y-2">
                  {category.documents.map((doc, docIdx) => (
                    <li key={docIdx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded">
                            {doc.type}
                          </span>
                          {doc.validity && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                              Validité: {doc.validity}
                            </span>
                          )}
                          {doc.amount && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                              {formatCurrency(doc.amount)}
                            </span>
                          )}
                        </div>
                        {doc.note && (
                          <p className="text-xs text-gray-500 mt-1">{doc.note}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500">
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <span>Slug: <code className="bg-gray-200 px-2 py-0.5 rounded">{concours.slug}</code></span>
          <span>Créé le: {formatDate(concours.created_at)}</span>
          <span>Mis à jour le: {formatDate(concours.updated_at)}</span>
          {concours.published_at && <span>Publié le: {formatDate(concours.published_at)}</span>}
          {concours.source && <span>Source: {concours.source}</span>}
        </div>
        {concours.keywords && concours.keywords.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {concours.keywords.map((keyword, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">
                #{keyword}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConcoursDetail;
